import React, {useEffect, useState} from 'react';
import {
  Button,
  Card,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  useMediaQuery,
} from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Moment from 'react-moment';
import {ticketsApi} from '../../../services/TicketsServices';
import {useNavigate} from 'react-router-dom';
import ErrorIcon from '@mui/icons-material/Error';
import {useTheme} from '@mui/material/styles';

export default function TicketList({status}) {
  const [tickets, setTickets] = useState([]);
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    setPage(0);
  }, [status]);

  useEffect(() => {
    fetchTicketList();
  }, [status, page, perPage]);

  const fetchTicketList = () => {
    ticketsApi('get', null, {state: status, page: page + 1, per_page: perPage}).then(res => {
      const data = res?.data || [];
      setTickets(data);
      setTotal(res?.meta?.total ?? data.length);
    });
  }

  const handleDetail = (id) => {
    navigate(`/tickets/${id}`);
  }

  const handleChangePage = (_, nextPage) => {
    setPage(nextPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const next = Number(event?.target?.value || 10);
    setPerPage(next);
    setPage(0);
  };

  return (
    <Box sx={{mb: 5}}>
      {isMobile ? (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
          {tickets?.length > 0 ? (
            tickets.map((ticket, index) => (
              <Card key={ticket?.id || index} className={'box-shadow'} sx={{p: 2}}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap'}}>
                  <Box>
                    <Typography sx={{fontSize: 14, fontWeight: 700}}>{ticket?.museum_name || '-'}</Typography>
                    <Typography sx={{fontSize: 13, color: '#6F7098'}}>{ticket?.ticket_no || '-'}</Typography>
                  </Box>
                  <Typography sx={{fontSize: 13, fontWeight: 700}}>
                    <Moment format={'ll'}>{ticket?.validity}</Moment>
                  </Typography>
                </Box>

                <Box sx={{mt: 1}}>
                  <Typography sx={{fontSize: 13}}>
                    Visitor: <span style={{fontWeight: 700}}>{ticket?.ticket_type || '-'}</span>
                  </Typography>
                  <Typography sx={{fontSize: 13}}>
                    Total Visitor: <span style={{fontWeight: 700}}>{ticket?.no_of_visitor ?? '-'}</span>
                  </Typography>
                </Box>

                <Button
                  sx={{mt: 1.5}}
                  fullWidth
                  size="small"
                  variant="outlined"
                  className={'text-capitalize'}
                  onClick={() => handleDetail(ticket.id)}
                >
                  View
                </Button>
              </Card>
            ))
          ) : (
            <Box className={'justify'} sx={{p: {xs: 2, sm: 4}}}>
              <Box className={'display-column'} sx={{textAlign: 'center'}}>
                <ErrorIcon sx={{fontSize: {xs: 42, sm: 50}, color: '#616b88'}}/>
                <Typography sx={{fontSize: {xs: 13, sm: 14}}}>
                  No tickets available in this category
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      ) : (
        <TableContainer className={'box-shadow'} component={Paper} sx={{width: '100%', overflowX: 'auto'}}>
          <Table sx={{minWidth: {xs: 900, sm: 650}}} aria-label="caption table">
            <TableHead>
              <TableRow>
                <TableCell align="left" sx={{color: '#B3B3B3', width: 20, whiteSpace: 'nowrap'}}>Sl.No</TableCell>
                <TableCell style={{color: '#B3B3B3'}} sx={{whiteSpace: 'nowrap'}}><span style={{color: '#616B88'}}>Name</span> / Museum</TableCell>
                <TableCell align="left" sx={{color: '#B3B3B3', whiteSpace: 'nowrap'}}>Ticket Number</TableCell>
                <TableCell align="left" sx={{color: '#B3B3B3', whiteSpace: 'nowrap'}}>Visitor</TableCell>
                <TableCell align="left" sx={{color: '#B3B3B3', whiteSpace: 'nowrap'}}>Total Visitor</TableCell>
                <TableCell align="left" sx={{color: '#B3B3B3', whiteSpace: 'nowrap'}}>Validity</TableCell>
                <TableCell align="right"/>
              </TableRow>
            </TableHead>
            {tickets?.length !== 0 ?
              <TableBody>
                {tickets?.length > 0 && tickets?.map((ticket, index) => (
                  <TableRow hover key={index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell sx={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                      <Box display='flex' flexDirection='column' sx={{ml: 1}}>
                        <Typography className={'text-capitalize'}
                                    sx={{fontSize: 14, fontWeight: 600}}>{ticket?.user_name || '-'}</Typography>
                        <Typography sx={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: '#6F7098'
                        }}>{ticket?.museum_name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{ticket?.ticket_no}</TableCell>
                    <TableCell className={'text-capitalize'}>{ticket?.ticket_type}</TableCell>
                    <TableCell>{ticket?.no_of_visitor}</TableCell>
                    <TableCell><Moment format={'ll'}>{ticket?.validity}</Moment></TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        className={'text-capitalize'}
                        onClick={() => handleDetail(ticket.id)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody> :
              <TableBody>
                <TableCell colSpan={6}>
                  <Box className={'justify'} sx={{p: {xs: 2, sm: 4}}}>
                    <Box className={'display-column'} sx={{textAlign: 'center'}}>
                      <ErrorIcon sx={{fontSize: {xs: 42, sm: 50}, color: '#616b88'}}/>
                      <Typography sx={{fontSize: {xs: 13, sm: 14}}}>
                        No tickets available in this category
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
              </TableBody>
            }
          </Table>
        </TableContainer>
      )}

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={perPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50]}
        sx={{
          '& .MuiTablePagination-toolbar': {
            flexWrap: 'wrap',
            justifyContent: {xs: 'center', sm: 'flex-end'},
            gap: 1,
          },
        }}
      />
    </Box>
  );
}